import { motion } from 'framer-motion'
import { useQuery } from '@tanstack/react-query'
import axios from 'axios'
import {
  Calendar,
  Clock,
  DollarSign,
  Brain,
  Target,
  Tag,
  FileText,
  Zap,
  TrendingUp,
  Users
} from 'lucide-react'

interface MarketPreviewProps {
  formData: {
    question: string
    description?: string
    category: string
    verifyRoute: string
    resolutionCriteria: string
    bettingEndsAt: string
    resolutionTime: string
    creationStake: number
  }
  marketId?: string // If this is an existing market, fetch real data
}

const categoryNames: Record<string, string> = {
  crypto: 'Crypto & DeFi',
  finance: 'Traditional Finance',
  social: 'Social Media',
  sports: 'Sports',
  entertainment: 'Entertainment',
  news: 'News & Events',
  government: 'Government',
  weather: 'Weather',
  technology: 'Technology',
  other: 'Other'
}

const routeNames: Record<string, string> = {
  crypto_price: 'Crypto Price Route',
  solana_defi: 'Solana DeFi Route',
  social_media: 'Social Media Route',
  traditional_finance: 'Traditional Finance Route',
  sports: 'Sports Route',
  news_events: 'News & Events Route',
  government: 'Government Route',
  weather: 'Weather Route',
  defi_analytics: 'DeFi Analytics Route',
  custom_api: 'Custom API Route'
}

export default function MarketPreview({ formData }: MarketPreviewProps) {
  const formatDate = (dateString: string) => {
    if (!dateString) return 'Not set'
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
  }

  const calculateMarketDuration = () => {
    if (!formData.bettingEndsAt) return 'Not set'
    const today = new Date()
    const endDate = new Date(formData.bettingEndsAt)
    const diffTime = endDate.getTime() - today.getTime()
    
    if (diffTime <= 0) return 'Already passed'
    
    // Calculate different time units
    const diffMinutes = Math.floor(diffTime / (1000 * 60))
    const diffHours = Math.floor(diffTime / (1000 * 60 * 60))
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
    
    // Flash market detection - if less than 2 hours, show precise timing
    if (diffMinutes < 120) {
      if (diffMinutes < 1) {
        return 'Flash market closing in < 1 min'
      } else if (diffMinutes < 60) {
        return `Flash market closing in ${diffMinutes} min`
      } else {
        const hours = Math.floor(diffMinutes / 60)
        const mins = diffMinutes % 60
        return mins > 0 
          ? `Flash market closing in ${hours}h ${mins}m`
          : `Flash market closing in ${hours}h`
      }
    }
    
    // Regular market timing for longer durations
    if (diffHours < 24) {
      return `${diffHours} hours`
    } else if (diffDays === 1) {
      return '1 day'
    } else {
      return `${diffDays} days`
    }
  }

  return (
    <div className="space-y-6">
      {/* Market Preview Card */}
      <div className="card p-8 bg-[#001233] border-[#052393]/40">
        <div className="text-center mb-8">
          <h2 className="text-3xl font-black text-white mb-2">Pool Preview</h2>
          <p className="text-gray-300 uppercase tracking-wider text-xs">This is how your pool will appear to users</p>
        </div>

        {/* Mock market card - simulating how it will look */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-[#001a3d] border border-[#2AAAEA]/30 hover:border-[#2AAAEA]/60 hover:shadow-lg hover:shadow-[#2AAAEA]/20 transition-all p-6 max-w-2xl mx-auto rounded-xl"
        >
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center space-x-2">
              <div className="px-2 py-1 rounded text-xs font-bold bg-[#052393]/40 text-cpx-light-blue uppercase tracking-wider">
                {categoryNames[formData.category] || formData.category}
              </div>
              <div className="px-2 py-1 rounded text-xs font-bold bg-[#009245]/20 text-cpx-green flex items-center">
                <Brain className="w-3 h-3 mr-1" />
                AI VERIFIED
              </div>
            </div>
            <div className="text-right text-sm text-gray-400">
              <div className="flex items-center space-x-1">
                <Clock className="w-4 h-4" />
                <span>{calculateMarketDuration()} to bet</span>
              </div>
            </div>
          </div>

          <h3 className="text-xl font-bold text-white mb-3 leading-tight">
            {formData.question || 'Your market question will appear here'}
          </h3>

          {formData.description && (
            <p className="text-gray-300 mb-4 text-sm leading-relaxed">
              {formData.description}
            </p>
          )}

          {/* Betting pools preview */}
          <div className="grid grid-cols-2 gap-4 mb-4">
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="bet-button-yes p-4 rounded-xl text-center"
            >
              <div className="text-sm font-medium opacity-90 mb-1">YES</div>
              <div className="text-lg font-bold">{(formData as any).outcome === 1 ? (formData.creationStake || 0) : 0} tokens</div>
              <div className="text-xs opacity-75">{(formData as any).outcome === 1 ? 'Your initial bet' : 'Awaiting bets'}</div>
            </motion.button>

            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="bet-button-no p-4 rounded-xl text-center"
            >
              <div className="text-sm font-medium opacity-90 mb-1">NO</div>
              <div className="text-lg font-bold">{(formData as any).outcome === 0 ? (formData.creationStake || 0) : 0} tokens</div>
              <div className="text-xs opacity-75">{(formData as any).outcome === 0 ? 'Your initial bet' : 'Awaiting bets'}</div>
            </motion.button>
          </div>

          <div className="flex items-center justify-between text-sm text-gray-400">
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-1">
                <TrendingUp className="w-4 h-4 text-cpx-light-blue" />
                <span>{formData.creationStake || 0} tokens bet</span>
              </div>
              <div className="flex items-center space-x-1">
                <Users className="w-4 h-4 text-cpx-light-blue" />
                <span>1 bettor (you)</span>
              </div>
            </div>
            <div className="text-cpx-green font-bold">
              Active
            </div>
          </div>
        </motion.div>
      </div>




    </div>
  )
}

